/**
 * Comprehensive logout utility for the application
 * Handles auth state clearing, localStorage management, and navigation
 */

import { NavigateFunction } from 'react-router-dom';
import { AuthContextType } from './context';
import { webStorage } from './storage';
import { getPropertyId } from '@/hooks/useApiSequencing';
import { deleteCookie } from '@/lib/utils/cookie';

export interface LogoutOptions {
  /**
   * Navigation function from react-router-dom
   */
  navigate?: NavigateFunction;

  /**
   * Auth context with logout method
   */
  authLogout: AuthContextType['logout'];

  /**
   * Property from URL params (optional)
   */
  property?: string;

  /**
   * If true, do not navigate to login page, 
   * use when clearing auth state on hard navigation
   * from the top url bar. 
   */
  doNotNav?: boolean;
}

/**
 * Performs a complete logout including:
 * - Clearing auth state via auth context
 * - Preserving specific localStorage items
 * - Clearing all localStorage and sessionStorage
 * - Restoring preserved items
 * - Navigating to property-specific login page
 */
export async function performLogout(options: LogoutOptions): Promise<void> {
  const { navigate, authLogout, property } = options;

  try {
    const preservedItems = clearUserItemsInLocalStorage();

    // Use auth context's logout method to properly clear auth state
    await authLogout();

    // Determine the property login path using preserved sitename or URL property
    const siteName = preservedItems.sitename || property;
    let loginPath = '/login'; // Default fallback

    if (siteName) {
      loginPath = `/guarantorportal/content3/access/${siteName}/guestlogin`;
    }

    // Navigate to property-specific login page
    if (!options.doNotNav) {
      navigate?.(loginPath);
    }
  } catch (error) {
    console.error('Error during logout:', error);
    // Even if there's an error, try to navigate to login
    const fallbackSiteName = localStorage.getItem('sitename') || property;
    const fallbackPath = fallbackSiteName
      ? `/guarantorportal/content3/access/${fallbackSiteName}/guestlogin`
      : '/login';
    navigate?.(fallbackPath);
  }
}

export function clearUserItemsInLocalStorage(): any {
  // Preserve specific localStorage items before clearing
  const preservedItems = {
    Property: localStorage.getItem('Property'),
    PropertyId: getPropertyId(),
    customtheme: localStorage.getItem('customtheme'),
    sitename: localStorage.getItem('sitename'),
    userpreferredlanguage: localStorage.getItem('user-preferred-language') 
  };

  // Preserve secure-login-processed keys from sessionStorage (for deduplication)
  const preservedSessionItems: Record<string, string> = {};
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key && key.startsWith('secure-login-processed-')) {
      const value = sessionStorage.getItem(key);
      if (value !== null) {
        preservedSessionItems[key] = value;
      }
    }
  }

  // Clear all localStorage and sessionStorage for a complete logout
  localStorage.clear();
  sessionStorage.clear();
  webStorage.removeYsiSessionID();
  deleteCookie('liui', '/');

  // Restore only the preserved items after complete clear
  Object.entries(preservedItems).forEach(([key, value]) => {
    if (value !== null) {
      localStorage.setItem(key, value);
    }
  });

  // Restore preserved sessionStorage items (secure-login deduplication keys)
  Object.entries(preservedSessionItems).forEach(([key, value]) => {
    sessionStorage.setItem(key, value);
  });

  return preservedItems;
}